import React, { useMemo } from "react";
import PropTypes from 'prop-types';
import { Chart as ChartJS, CategoryScale, LineController, LineElement, PointElement, LinearScale, Title, Tooltip, BarElement} from 'chart.js';
import { Box, Flex, Heading, Collapse, Text, Button } from '@chakra-ui/react';
import './style.css';

ChartJS.register(CategoryScale, LineController, LineElement, PointElement, LinearScale, BarElement, Tooltip, Title);

const ProcyonChart = ({ 
  chartData, 
  labelsChartData, 
  tooltipChartData, 
  titleBenchmark, 
  levelsBenchmark, 
  expandedIndex,      // Hooked up to parent state to resolve synchronization
  setExpandedIndex,   // Hooked up to parent state to resolve synchronization
  activeTab, 
  setActiveTab 
}) => {

  const arrayColors = useMemo(() => { return ["#767171", "#FFC000", "#C00000", "#385723", "#5B9BD5"]; }, []);

  const handleBoxClick = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  // Removed internal argument definitions to prevent parameter shadowing bugs
  const buildBoxInfoMobile = () => {
  const gapSize = '16px'; 
  
  // 1. Fetch raw unmapped data from the selected dataset
  const rawSelectedData = expandedIndex !== null && tooltipChartData ? tooltipChartData[expandedIndex] : null;

  // 2. Invert the data mapping: example1 reads example3 and vice versa
  const selectedData = rawSelectedData 
    ? {
        ...rawSelectedData,
        example1: rawSelectedData.example3, // Example 1 tab now shows Example 3 data
        example3: rawSelectedData.example1  // Example 3 tab now shows Example 1 data
      }
    : null;

  // Generates the list of valid tabs for the selected item
  const getValidTabs = (data) => {
    if (!data) return [];
    
    // UI tabs layout remains sequentially ordered 1, 2, 3
    const tabsConfig = [
      { id: 'example1', label: 'Example 1' },
      { id: 'example2', label: 'Example 2' },
      { id: 'example3', label: 'Example 3' },
    ];
    
    // The filter now processes the remapped inverted data from step 2
    return tabsConfig.filter(tab => {
      const spec = data[tab.id];
      return spec && (spec.cpu?.trim() || spec.igpu?.trim() || spec.dgpu?.trim());
    });
  };

  const validTabs = getValidTabs(selectedData);
  
  // Fallback structural fix for managing active tabs seamlessly
  const currentTabId = validTabs.some(t => t.id === activeTab) 
    ? activeTab 
    : (validTabs[0]?.id || null);

  const activeSpecs = selectedData && currentTabId ? selectedData[currentTabId] : null;

  return (
    <Box w="100%">
      {/* Top Grid of Clickable Boxes */}
      <Flex wrap="wrap" gap={gapSize} w="100%" className="mainContainerMobile">
        {tooltipChartData && tooltipChartData.map((info, index) => {
          const isSelected = expandedIndex === index;
          return (
            <Box
              key={index}
              flex={{ base: "1 1 100%", md: `0 0 calc(50% - (${gapSize} / 2))` }}
              boxSizing="border-box"
              border="1px solid"
              borderColor={isSelected ? arrayColors[index] : "#E7E7E7"}
              borderRadius="md"
              p="16px"
              textAlign="left"
              cursor="pointer"
              onClick={() => handleBoxClick(index)}
              _hover={{ borderColor: arrayColors[index], shadow: "sm" }}
              transition="all 0.2s"
              className="containerBoxMobile"
            >
              <Flex align="center" gap="12px" h="100%" className="titleContainerBoxMobile">
                <Box w="12px" h="12px" borderRadius="full" bg={arrayColors[index]} className="titleContainerBoxColor" />
                <Heading as="h4" fontSize="16px" fontWeight="600" className="titleTextBoxMobile">
                  {info.title}
                </Heading>
              </Flex>
            </Box>
          );
        })}
      </Flex>

      {/* Lower Expanded Section */}
      <Collapse in={expandedIndex !== null} animateOpacity>
        {selectedData && (
          <Box 
            w="100%" mt="24px" p="24px" border="1px solid" borderColor="#E7E7E7" 
            borderLeftWidth="6px" borderLeftColor={arrayColors[expandedIndex]} borderRadius="md"
            bg="white" boxShadow="sm" textAlign="left"
          >
            {/* 1. Box Title */}
            <Flex direction="column" align="flex-start" gap="8px" mb="20px">
              <Heading as="h5" fontSize="18px" fontWeight="bold" color="gray.800">
                {selectedData.title}
              </Heading>
            </Flex>

            {/* 2. Tabs Menu */}
            {validTabs.length > 0 && (
              <Flex gap="8px" mb="20px" wrap="wrap">
                {validTabs.map((tab) => {
                  const isTabActive = currentTabId === tab.id;
                  return (
                    <Button
                      key={tab.id}
                      size="sm"
                      onClick={() => setActiveTab(tab.id)}
                      bg={isTabActive ? arrayColors[expandedIndex] : "gray.100"}
                      color={isTabActive ? "white" : "gray.700"}
                      _hover={{ bg: isTabActive ? arrayColors[expandedIndex] : "gray.200" }}
                      borderRadius="md"
                      fontSize="13px"
                      fontWeight="600"
                    >
                      {tab.label}
                    </Button>
                  );
                })}
              </Flex>
            )}

            {/* 3. Specs of the Active Tab */}
            <Box mb="20px" bg="gray.50" p="16px" borderRadius="md">
              {activeSpecs?.cpu && activeSpecs.cpu.trim() !== '' && (
                <Text fontSize="14px" mb="8px" color="gray.600">
                  <Text as="span" color="gray.900" fontWeight="bold">CPU: </Text>{activeSpecs.cpu}
                </Text>
              )}
              {activeSpecs?.igpu && activeSpecs.igpu.trim() !== '' && (
                <Text fontSize="14px" mb="8px" color="gray.600">
                  <Text as="span" color="gray.900" fontWeight="bold">iGPU: </Text>{activeSpecs.igpu}
                </Text>
              )}
              {activeSpecs?.dgpu && activeSpecs.dgpu.trim() !== '' && (
                <Text fontSize="14px" color="gray.600">
                  <Text as="span" color="gray.900" fontWeight="bold">dGPU: </Text>{activeSpecs.dgpu}
                </Text>
              )}
              {/* Fallback layout */}
              {(!activeSpecs || (!activeSpecs.cpu && !activeSpecs.igpu && !activeSpecs.dgpu)) && (
                <Text fontSize="14px" color="gray.400" fontStyle="italic">
                  No specifications available for this configuration.
                </Text>
              )}
            </Box>

            {/* 4. Bottom Description */}
            {selectedData.content2 && selectedData.content2.trim() !== '' && (
              <Box borderTop="1px solid" borderColor="gray.100" pt="16px">
                <Text fontSize="14px" color="gray.600" lineHeight="tall">
                  <Text as="b" color="gray.800">Typical usage: </Text>{selectedData.content2}
                </Text>
              </Box>
            )}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

  return (
    buildBoxInfoMobile()
  );
};

ProcyonChart.propTypes = {
  chartData: PropTypes.array.isRequired
};

export default ProcyonChart;